import React, { useState, useCallback } from 'react';
import { UploadCloud, FileText, X, Loader2, CheckCircle2, File } from 'lucide-react';
import { useForm } from '../context/FormContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { computeFieldCompleteness } from '../lib/fieldCompleteness';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Smooth progress indicator - fills gradually over expected time
const ProgressIndicator = ({ progress }) => {
  return (
    <div className="bg-slate-50 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-center gap-3 mb-2">
        <Loader2 className="w-6 h-6 animate-spin brand-text" />
        <span className="font-semibold text-slate-700 text-lg">Data Extraction in Progress</span>
      </div>
      
      <p className="text-center text-slate-500 text-sm">
        Extracting uploaded information and formatting into a proposal
      </p>
      
      {/* Smooth progress bar */}
      <div className="mt-4 h-3 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className="h-full brand-bg transition-all duration-300 ease-out"
          style={{ width: `${Math.min(progress, 95)}%` }}
        />
      </div>
      
      <p className="text-center text-slate-400 text-xs">
        {progress < 95 ? 'This usually takes 10-20 seconds...' : 'Almost done...'}
      </p>
    </div>
  );
};

export const UploadStep = ({ onComplete }) => {
  const { applyExtractedData, setIsProcessing, isProcessing } = useForm();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pastedText, setPastedText] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);

  const isValidFileType = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    return validTypes.includes(file.type);
  };

  // Compress large images to prevent timeouts
  const compressImage = (file, maxSizeMB = 2) => {
    return new Promise((resolve) => {
      if (file.type === 'application/pdf' || file.size <= maxSizeMB * 1024 * 1024) {
        resolve(file);
        return;
      }
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const maxDim = 2048;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })),
          'image/jpeg',
          0.85
        );
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter(isValidFileType);
    
    if (files.length === 0) {
      toast.error('Please upload images (JPEG, PNG, WEBP) or PDF files');
      return;
    }
    
    setUploadedFiles(prev => [...prev, ...files]);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files).filter(isValidFileType);
    setUploadedFiles(prev => [...prev, ...files]);
  }, []);

  const removeFile = useCallback((index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Smooth progress that fills over expected time
  const startSmoothProgress = () => {
    setExtractionProgress(0);
    const expectedTime = 20000;
    const interval = 200;
    const increment = (95 / (expectedTime / interval));
    
    const progressInterval = setInterval(() => {
      setExtractionProgress(prev => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95;
        }
        return prev + increment;
      });
    }, interval);
    
    return progressInterval;
  };

  const hasFiles = uploadedFiles.length > 0;
  const hasText = pastedText.trim().length > 0;
  const hasAnything = hasFiles || hasText;

  const processAll = async () => {
    if (!hasAnything) {
      toast.error('Please upload files or paste text');
      return;
    }

    setIsProcessing(true);
    const progressInterval = startSmoothProgress();
    // Safety net: if the axios call somehow hangs past the timeout (backgrounded
    // tab, browser paused JS timers, mobile Chrome throttling), surface a
    // "still working" hint after 45s so the user isn't staring at a stuck
    // progress bar with no feedback. Cleared in `finally` regardless of outcome.
    const stillWorkingTimer = setTimeout(() => {
      toast.message('Still working — this one is taking longer than usual. Please wait or leave the tab open.', {
        duration: 12000,
      });
    }, 45000);
    // Second hint after 2 minutes — recommend the user tries pasted text as
    // a workaround if extraction is genuinely stuck.
    const veryStuckTimer = setTimeout(() => {
      toast.message('Extraction is unusually slow. If it doesn\'t finish soon, cancel and paste the text instead.', {
        duration: 15000,
      });
    }, 120000);
    
    try {
      let mergedData = {};
      
      // Separate PDFs and images
      const pdfFiles = uploadedFiles.filter(f => f.type === 'application/pdf');
      const imageFiles = uploadedFiles.filter(f => f.type !== 'application/pdf');
      
      // Process PDFs
      for (const pdf of pdfFiles) {
        const formData = new FormData();
        formData.append('file', pdf);
        
        const response = await axios.post(`${API_URL}/extract/pdf`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000
        });
        
        if (response.data.success) {
          for (const [key, value] of Object.entries(response.data.data)) {
            if (value !== null && (!(key in mergedData) || mergedData[key] === null)) {
              mergedData[key] = value;
            }
          }
        }
      }
      
      // Process images
      if (imageFiles.length > 0) {
        const formData = new FormData();
        for (const file of imageFiles) {
          const compressed = await compressImage(file);
          formData.append('files', compressed);
        }

        const response = await axios.post(`${API_URL}/extract/images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000
        });

        if (response.data.success) {
          for (const [key, value] of Object.entries(response.data.data)) {
            if (value !== null && (!(key in mergedData) || mergedData[key] === null)) {
              mergedData[key] = value;
            }
          }
        }
      }

      // Process pasted text (overrides image data where both have values)
      if (hasText) {
        const response = await axios.post(`${API_URL}/extract/text`, {
          text: pastedText
        }, { timeout: 120000 });

        if (response.data.success) {
          for (const [key, value] of Object.entries(response.data.data)) {
            // Skip null, empty strings, zero, empty arrays, and empty objects
            if (value === null || value === '' || value === 0) continue;
            if (Array.isArray(value) && value.length === 0) continue;
            if (typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(v => !v)) continue;
            mergedData[key] = value;
          }
        }
      }

      // Complete progress
      clearInterval(progressInterval);
      setExtractionProgress(100);
      await new Promise(resolve => setTimeout(resolve, 300));

      await applyExtractedData(mergedData);

      // Summary toast: count how many of the "important" fields the LLM caught and call
      // out the most useful gaps so the dealer knows where to look first.
      const { got, total, missing } = computeFieldCompleteness(mergedData);
      if (missing.length === 0) {
        toast.success(`All ${total} fields extracted \u2713`);
      } else {
        const labels = missing.map((m) => m.label);
        const preview = labels.slice(0, 4).join(', ') + (labels.length > 4 ? ` +${labels.length - 4} more` : '');
        toast.success(`Extracted ${got} of ${total} fields. Missing: ${preview}`, { duration: 6000 });
      }
      onComplete();
    } catch (error) {
      console.error('Extraction error:', error);
      // Differentiate timeout vs backend error vs network drop so the user
      // knows what to do next instead of seeing a generic "Failed to extract data".
      let msg;
      if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        msg = 'Extraction is taking longer than expected. Try again, use a smaller/clearer PDF, or paste the text instead.';
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      } else if (error.response?.status === 429) {
        msg = 'The AI service is busy right now — please wait a moment and try again.';
      } else if (!error.response) {
        msg = 'Network issue — please check your connection and try again.';
      } else {
        msg = 'Failed to extract data. Please try again or fill the form manually.';
      }
      toast.error(msg, { duration: 8000 });
    } finally {
      clearInterval(progressInterval);
      clearTimeout(stillWorkingTimer);
      clearTimeout(veryStuckTimer);
      setIsProcessing(false);
      setExtractionProgress(0);
    }
  };

  const skipExtraction = () => {
    toast.info('Skipping extraction - you can fill the form manually');
    onComplete();
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in-up" data-testid="upload-step">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Import Your Application Data
        </h2>
        <p className="text-slate-600">
          Upload images, PDFs, and/or paste text — we'll extract and merge everything together
        </p>
      </div>

      {/* Show progress indicator when extracting */}
      {isProcessing && (
        <div className="mb-6">
          <ProgressIndicator progress={extractionProgress} />
        </div>
      )}

      {/* Hide content when processing */}
      {!isProcessing && (
        <div className="space-y-6">
          {/* File Upload Area */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
              <UploadCloud className="w-4 h-4" />
              Upload Files
              <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <div
              className={`border-2 border-dashed rounded-xl p-6 md:p-8 text-center transition-all cursor-pointer ${
                isDragging 
                  ? 'brand-border brand-bg-light' 
                  : 'border-slate-300 hover:brand-border hover:brand-bg-light/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,application/pdf"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="file-input"
              />
              <UploadCloud className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'brand-text' : 'text-slate-400'}`} />
              <p className="text-base font-medium text-slate-700 mb-1">
                Drag & drop files here
              </p>
              <p className="text-sm text-slate-500">
                or click to browse (JPEG, PNG, WEBP, PDF)
              </p>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="uploaded-files-list">
                {uploadedFiles.map((file, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg"
                    data-testid={`uploaded-file-${index}`}
                  >
                    <div className="flex items-center gap-2">
                      {file.type === 'application/pdf' ? (
                        <File className="w-4 h-4 text-red-500" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 brand-text" />
                      )}
                      <span className="text-sm text-slate-700 truncate max-w-[200px]">{file.name}</span>
                      <span className="text-xs text-slate-400">
                        ({(file.size / 1024).toFixed(0)} KB)
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="p-1 hover:bg-slate-200 rounded"
                      data-testid={`remove-file-${index}`}
                    >
                      <X className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Text Paste Area */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Paste Text
              <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <Textarea
              placeholder="Paste vehicle details, finance quotes, or any application text here...&#10;&#10;e.g. Vehicle: Ford Focus 1.0 EcoBoost, Price: £15,000, Term: 60 months..."
              className="min-h-[120px] text-sm"
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              data-testid="paste-textarea"
            />
            {hasFiles && hasText && (
              <p className="text-xs text-slate-500 mt-1.5">
                Both files and text will be processed and merged together
              </p>
            )}
          </div>

          {/* Extract Button */}
          <Button
            onClick={processAll}
            disabled={!hasAnything || isProcessing}
            className="w-full brand-bg text-white font-semibold py-3 text-base"
            data-testid="extract-all-btn"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Extract Data
                {hasFiles && hasText && ' (Files + Text)'}
                {hasFiles && !hasText && ` (${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''})`}
                {!hasFiles && hasText && ' (Text)'}
              </>
            )}
          </Button>

          {/* Skip option */}
          <div className="pt-4 border-t border-slate-200">
            <p className="text-center text-sm text-slate-600 mb-3">Or start without document extraction</p>
            <Button
              onClick={skipExtraction}
              variant="outline"
              className="w-full border-2 brand-border brand-text hover:brand-bg-light font-semibold py-3"
              data-testid="skip-extraction-btn"
            >
              Fill Form Manually
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadStep;
